import React, { useState, useEffect } from 'react';
import './DRPModule.css';
import collegeConfig from '../../config/collegeConfig';
import { 
  FaArrowLeft, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaHospital,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaFileAlt,
  FaChartLine,
  FaAward,
  FaPlus,
  FaDownload
} from 'react-icons/fa';

const DRPModule = ({ onNavigate }) => {
  const [user, setUser] = useState(null);
  const [postings, setPostings] = useState([]);
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // overview, attendance, activities, reports, performance
  
  // Modal states
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  
  // Form states
  const [attendanceForm, setAttendanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    checkInTime: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
  });
  
  const [activityForm, setActivityForm] = useState({
    date: new Date().toISOString().split('T')[0],
    tasks: '',
    patientsSeen: 0,
    proceduresPerformed: '',
    notes: '',
    learningPoints: ''
  });
  
  const [reportForm, setReportForm] = useState({
    weekNumber: 1,
    weekStartDate: '',
    weekEndDate: '',
    summary: '',
    keyLearnings: '',
    challenges: '',
    casesHandled: 0
  });

  // Fetch user data
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Fetch DRP postings
  useEffect(() => {
    if (user) {
      fetchPostings();
    }
  }, [user]);

  const fetchPostings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${collegeConfig.api.baseURL}/drp/my-postings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPostings(data.postings);
        
        // Auto-select first active posting
        const activePosting = data.postings.find(p => p.isActive && !p.isCompleted);
        if (activePosting) {
          setSelectedPosting(activePosting);
        } else if (data.postings.length > 0) {
          setSelectedPosting(data.postings[0]);
        }
      } else {
        setError('Failed to fetch DRP postings');
      }
    } catch (error) {
      console.error('Error fetching postings:', error);
      setError('Error loading DRP data');
    } finally {
      setLoading(false);
    }
  };

  const submitAttendance = async () => {
    if (!selectedPosting) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Get geolocation if available
      let location = {};
      if (navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              location = {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              };
              resolve();
            },
            () => resolve() // Continue even if location fails
          );
        });
      }
      
      const response = await fetch(
        `${collegeConfig.api.baseURL}/drp/posting/${selectedPosting._id}/attendance`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...attendanceForm,
            ...location
          })
        }
      );
      
      if (response.ok) {
        alert('✅ Attendance marked successfully!');
        setShowAttendanceModal(false);
        fetchPostings(); // Refresh data
        setAttendanceForm({
          date: new Date().toISOString().split('T')[0],
          checkInTime: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
        });
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to mark attendance');
      }
    } catch (error) {
      console.error('Error submitting attendance:', error);
      alert('Error submitting attendance');
    }
  };

  const submitDailyActivity = async () => {
    if (!selectedPosting) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${collegeConfig.api.baseURL}/drp/posting/${selectedPosting._id}/daily-activity`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...activityForm,
            proceduresPerformed: activityForm.proceduresPerformed.split(',').map(p => p.trim()).filter(p => p)
          })
        }
      );
      
      if (response.ok) {
        alert('✅ Daily activity submitted successfully!');
        setShowActivityModal(false);
        fetchPostings();
        setActivityForm({
          date: new Date().toISOString().split('T')[0],
          tasks: '',
          patientsSeen: 0,
          proceduresPerformed: '',
          notes: '',
          learningPoints: ''
        });
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to submit activity');
      }
    } catch (error) {
      console.error('Error submitting activity:', error);
      alert('Error submitting activity');
    }
  };

  const submitWeeklyReport = async () => {
    if (!selectedPosting) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${collegeConfig.api.baseURL}/drp/posting/${selectedPosting._id}/weekly-report`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(reportForm)
        }
      );
      
      if (response.ok) {
        alert('✅ Weekly report submitted successfully!');
        setShowReportModal(false);
        fetchPostings();
        setReportForm({
          weekNumber: reportForm.weekNumber + 1,
          weekStartDate: '',
          weekEndDate: '',
          summary: '',
          keyLearnings: '',
          challenges: '',
          casesHandled: 0
        });
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to submit report');
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Error submitting report');
    }
  };

  const downloadCertificate = () => {
    if (selectedPosting?.certificateUrl) {
      window.open(`${collegeConfig.api.baseURL}${selectedPosting.certificateUrl}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="drpcontainer_88748">
        <div className="loadingspinner_88748">
          <div className="spinner_88748"></div>
          <p>Loading DRP Data...</p>
        </div>
      </div>
    );
  }

  if (!selectedPosting && postings.length === 0) {
    return (
      <div className="drpcontainer_88748">
        <div className="drpheader_88748">
          <button className="backbutton_88748" onClick={() => onNavigate('dashboard')}>
            <FaArrowLeft /> Back to Dashboard
          </button>
          <h1>District Residency Programme</h1>
        </div>
        
        <div className="nopostings_88748">
          <FaHospital size={60} color="#ccc" />
          <h2>No DRP Postings</h2>
          <p>You don't have any DRP postings assigned yet.</p>
          <p>Please contact your administrator for posting assignment.</p>
        </div>
      </div>
    );
  }

  const stats = selectedPosting?.attendanceStats || {
    totalDays: 0,
    presentDays: 0,
    attendancePercentage: 0
  };

  return (
    <div className="drpcontainer_88748">
      {/* Header */}
      <div className="drpheader_88748">
        <button className="backbutton_88748" onClick={() => onNavigate('dashboard')}>
          <FaArrowLeft /> Back
        </button>
        <div className="headercontent_88748">
          <h1>District Residency Programme</h1>
          <p>Track your district posting activities and performance</p>
        </div>
      </div>

      {/* Posting Selector */}
      {postings.length > 1 && (
        <div className="postingselector_88748">
          <label>Select Posting:</label>
          <select 
            value={selectedPosting?._id || ''} 
            onChange={(e) => {
              const posting = postings.find(p => p._id === e.target.value);
              setSelectedPosting(posting);
            }}
          >
            {postings.map(posting => (
              <option key={posting._id} value={posting._id}>
                {posting.district.name} - {posting.postingDetails.department} 
                ({new Date(posting.postingDetails.startDate).toLocaleDateString()})
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedPosting && (
        <>
          {/* Posting Info Card */}
          <div className="postinginfocard_88748">
            <div className="inforow_88748">
              <div className="infoitem_88748">
                <FaMapMarkerAlt className="infoicon_88748" />
                <div>
                  <label>District</label>
                  <p>{selectedPosting.district.name}, {selectedPosting.district.state}</p>
                </div>
              </div>
              
              <div className="infoitem_88748">
                <FaHospital className="infoicon_88748" />
                <div>
                  <label>Hospital</label>
                  <p>{selectedPosting.district.hospitalName}</p>
                </div>
              </div>
              
              <div className="infoitem_88748">
                <FaCalendarAlt className="infoicon_88748" />
                <div>
                  <label>Duration</label>
                  <p>
                    {new Date(selectedPosting.postingDetails.startDate).toLocaleDateString()} - 
                    {new Date(selectedPosting.postingDetails.endDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <div className="infoitem_88748">
                <label>Department</label>
                <p className="departmentbadge_88748">{selectedPosting.postingDetails.department}</p>
              </div>
            </div>
            
            {/* Status Badge */}
            <div className="statusbadgecontainer_88748">
              <span className={`statusbadge_88748 ${selectedPosting.verificationStatus}`}>
                {selectedPosting.verificationStatus.replace('_', ' ').toUpperCase()}
              </span>
              {selectedPosting.isCompleted && (
                <span className="statusbadge_88748 completed_88748">
                  <FaCheckCircle /> COMPLETED
                </span>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="quickstats_88748">
            <div className="statcard_88748">
              <div className="staticon_88748" style={{ background: '#3498DB' }}>
                <FaCalendarAlt />
              </div>
              <div className="statcontent_88748">
                <h3>{stats.totalDays}</h3>
                <p>Total Days</p>
              </div>
            </div>
            
            <div className="statcard_88748">
              <div className="staticon_88748" style={{ background: '#2ECC71' }}>
                <FaCheckCircle />
              </div>
              <div className="statcontent_88748">
                <h3>{stats.presentDays}</h3>
                <p>Present Days</p>
              </div>
            </div>
            
            <div className="statcard_88748">
              <div className="staticon_88748" style={{ background: '#F39C12' }}>
                <FaChartLine />
              </div>
              <div className="statcontent_88748">
                <h3>{stats.attendancePercentage.toFixed(1)}%</h3>
                <p>Attendance</p>
              </div>
            </div>
            
            <div className="statcard_88748">
              <div className="staticon_88748" style={{ background: '#9B59B6' }}>
                <FaFileAlt />
              </div>
              <div className="statcontent_88748">
                <h3>{selectedPosting.dailyActivities?.length || 0}</h3>
                <p>Activities Logged</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {!selectedPosting.isCompleted && (
            <div className="actionbuttons_88748">
              <button 
                className="actionbtn_88748 primary_88748"
                onClick={() => setShowAttendanceModal(true)}
              >
                <FaCheckCircle /> Mark Attendance
              </button>
              
              <button 
                className="actionbtn_88748 secondary_88748"
                onClick={() => setShowActivityModal(true)}
              >
                <FaPlus /> Log Activity
              </button>
              
              <button 
                className="actionbtn_88748 tertiary_88748"
                onClick={() => setShowReportModal(true)}
              >
                <FaFileAlt /> Submit Weekly Report
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="drptabs_88748">
            <button 
              className={activeTab === 'overview' ? 'active_88748' : ''}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={activeTab === 'attendance' ? 'active_88748' : ''}
              onClick={() => setActiveTab('attendance')}
            >
              Attendance
            </button>
            <button 
              className={activeTab === 'activities' ? 'active_88748' : ''}
              onClick={() => setActiveTab('activities')}
            >
              Daily Activities
            </button>
            <button 
              className={activeTab === 'reports' ? 'active_88748' : ''}
              onClick={() => setActiveTab('reports')}
            >
              Weekly Reports
            </button>
            <button 
              className={activeTab === 'performance' ? 'active_88748' : ''}
              onClick={() => setActiveTab('performance')}
            >
              Performance
            </button>
          </div>

          {/* Tab Content */}
          <div className="tabcontent_88748">
            {activeTab === 'overview' && (
              <div className="overviewtab_88748">
                <h3>Posting Overview</h3>
                <div className="overviewgrid_88748">
                  <div className="overviewitem_88748">
                    <label>Semester</label>
                    <p>{selectedPosting.postingDetails.semester}</p>
                  </div>
                  <div className="overviewitem_88748">
                    <label>Academic Year</label>
                    <p>{selectedPosting.postingDetails.academicYear}</p>
                  </div>
                  <div className="overviewitem_88748">
                    <label>Total Days</label>
                    <p>{selectedPosting.postingDetails.totalDays} days</p>
                  </div>
                  <div className="overviewitem_88748">
                    <label>Status</label>
                    <p>{selectedPosting.verificationStatus.replace('_', ' ')}</p>
                  </div>
                </div>
                
                {selectedPosting.isCompleted && selectedPosting.certificateUrl && (
                  <div className="certificatesection_88748">
                    <FaAward size={40} color="#F39C12" />
                    <h3>Certificate Available</h3>
                    <p>Certificate No: {selectedPosting.certificateNumber}</p>
                    <button className="downloadcertbtn_88748" onClick={downloadCertificate}>
                      <FaDownload /> Download Certificate
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'attendance' && (
              <div className="attendancetab_88748">
                <h3>Attendance Records</h3>
                {selectedPosting.attendance && selectedPosting.attendance.length > 0 ? (
                  <div className="attendancelist_88748">
                    {selectedPosting.attendance
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((att, index) => (
                      <div key={index} className="attendanceitem_88748">
                        <div className="attdate_88748">
                          <FaCalendarAlt />
                          <span>{new Date(att.date).toLocaleDateString()}</span>
                        </div>
                        <div className="attstatus_88748">
                          <span className={`statuspill_88748 ${att.status}`}>
                            {att.status === 'present' ? <FaCheckCircle /> : <FaTimesCircle />}
                            {att.status}
                          </span>
                        </div>
                        <div className="atttime_88748">
                          <FaClock />
                          <span>{att.checkInTime}</span>
                        </div>
                        <div className="attverification_88748">
                          {att.verifiedByDRPC ? (
                            <span className="verified_88748">✓ Verified by DRPC</span>
                          ) : (
                            <span className="pending_88748">⏳ Pending Verification</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="nodata_88748">No attendance records yet</p>
                )}
              </div>
            )}

            {activeTab === 'activities' && (
              <div className="activitiestab_88748">
                <h3>Daily Activities</h3>
                {selectedPosting.dailyActivities && selectedPosting.dailyActivities.length > 0 ? (
                  <div className="activitieslist_88748">
                    {selectedPosting.dailyActivities
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((activity, index) => (
                      <div key={index} className="activityitem_88748">
                        <div className="activityheader_88748">
                          <h4>{new Date(activity.date).toLocaleDateString()}</h4>
                          <span className="patientscount_88748">{activity.patientsSeen} patients</span>
                        </div>
                        <div className="activitybody_88748">
                          <p><strong>Tasks:</strong> {activity.tasks}</p>
                          {activity.proceduresPerformed && activity.proceduresPerformed.length > 0 && (
                            <p><strong>Procedures:</strong> {activity.proceduresPerformed.join(', ')}</p>
                          )}
                          {activity.learningPoints && (
                            <p><strong>Learning Points:</strong> {activity.learningPoints}</p>
                          )}
                          {activity.notes && (
                            <p><strong>Notes:</strong> {activity.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="nodata_88748">No activities logged yet</p>
                )}
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="reportstab_88748">
                <h3>Weekly Reports</h3>
                {selectedPosting.weeklyReports && selectedPosting.weeklyReports.length > 0 ? (
                  <div className="reportslist_88748">
                    {selectedPosting.weeklyReports
                      .sort((a, b) => b.weekNumber - a.weekNumber)
                      .map((report, index) => (
                      <div key={index} className="reportitem_88748">
                        <div className="reportheader_88748">
                          <h4>Week {report.weekNumber}</h4>
                          <span>
                            {new Date(report.weekStartDate).toLocaleDateString()} - 
                            {new Date(report.weekEndDate).toLocaleDateString()}
                          </span>
                          {report.verifiedByDRPC && (
                            <span className="verifiedbadge_88748">✓ Verified</span>
                          )}
                        </div>
                        <div className="reportbody_88748">
                          <p><strong>Summary:</strong> {report.summary}</p>
                          <p><strong>Key Learnings:</strong> {report.keyLearnings}</p>
                          <p><strong>Challenges:</strong> {report.challenges}</p>
                          <p><strong>Cases Handled:</strong> {report.casesHandled}</p>
                          {report.drpcFeedback && (
                            <div className="drpcfeedback_88748">
                              <strong>DRPC Feedback:</strong>
                              <p>{report.drpcFeedback}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="nodata_88748">No weekly reports submitted yet</p>
                )}
              </div>
            )}

            {activeTab === 'performance' && (
              <div className="performancetab_88748">
                <h3>Performance Evaluation</h3>
                
                {selectedPosting.performance.finalScore ? (
                  <div className="performancesummary_88748">
                    <div className="scorecard_88748">
                      <h2>{selectedPosting.performance.finalScore}/100</h2>
                      <p>Final Score</p>
                      <span className="gradebadge_88748">{selectedPosting.performance.grade}</span>
                    </div>
                    
                    <div className="evaluationdetails_88748">
                      {selectedPosting.performance.drpcScore && (
                        <div className="evaluationsection_88748">
                          <h4>DRPC Evaluation</h4>
                          <p><strong>Score:</strong> {selectedPosting.performance.drpcScore}/100</p>
                          <p><strong>Remarks:</strong> {selectedPosting.performance.drpcRemarks}</p>
                          <p className="evaldate_88748">
                            Evaluated on: {new Date(selectedPosting.performance.drpcEvaluationDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      
                      {selectedPosting.performance.teacherScore && (
                        <div className="evaluationsection_88748">
                          <h4>Teacher Evaluation</h4>
                          <p><strong>Score:</strong> {selectedPosting.performance.teacherScore}/100</p>
                          <p><strong>Remarks:</strong> {selectedPosting.performance.teacherRemarks}</p>
                          <p className="evaldate_88748">
                            Evaluated on: {new Date(selectedPosting.performance.teacherEvaluationDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      
                      {(selectedPosting.performance.clinicalSkills || 
                        selectedPosting.performance.communication ||
                        selectedPosting.performance.professionalism) && (
                        <div className="evaluationsection_88748">
                          <h4>Detailed Assessment</h4>
                          <div className="criteriagrid_88748">
                            {selectedPosting.performance.clinicalSkills && (
                              <div className="criteriaitem_88748">
                                <label>Clinical Skills</label>
                                <div className="scorebar_88748">
                                  <div 
                                    className="scorefill_88748" 
                                    style={{ width: `${selectedPosting.performance.clinicalSkills * 10}%` }}
                                  ></div>
                                </div>
                                <span>{selectedPosting.performance.clinicalSkills}/10</span>
                              </div>
                            )}
                            {selectedPosting.performance.communication && (
                              <div className="criteriaitem_88748">
                                <label>Communication</label>
                                <div className="scorebar_88748">
                                  <div 
                                    className="scorefill_88748" 
                                    style={{ width: `${selectedPosting.performance.communication * 10}%` }}
                                  ></div>
                                </div>
                                <span>{selectedPosting.performance.communication}/10</span>
                              </div>
                            )}
                            {selectedPosting.performance.professionalism && (
                              <div className="criteriaitem_88748">
                                <label>Professionalism</label>
                                <div className="scorebar_88748">
                                  <div 
                                    className="scorefill_88748" 
                                    style={{ width: `${selectedPosting.performance.professionalism * 10}%` }}
                                  ></div>
                                </div>
                                <span>{selectedPosting.performance.professionalism}/10</span>
                              </div>
                            )}
                            {selectedPosting.performance.teamwork && (
                              <div className="criteriaitem_88748">
                                <label>Teamwork</label>
                                <div className="scorebar_88748">
                                  <div 
                                    className="scorefill_88748" 
                                    style={{ width: `${selectedPosting.performance.teamwork * 10}%` }}
                                  ></div>
                                </div>
                                <span>{selectedPosting.performance.teamwork}/10</span>
                              </div>
                            )}
                            {selectedPosting.performance.initiative && (
                              <div className="criteriaitem_88748">
                                <label>Initiative</label>
                                <div className="scorebar_88748">
                                  <div 
                                    className="scorefill_88748" 
                                    style={{ width: `${selectedPosting.performance.initiative * 10}%` }}
                                  ></div>
                                </div>
                                <span>{selectedPosting.performance.initiative}/10</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="nodata_88748">Performance evaluation pending</p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      {showAttendanceModal && (
        <div className="modaloverlay_88748" onClick={() => setShowAttendanceModal(false)}>
          <div className="modalcontent_88748" onClick={(e) => e.stopPropagation()}>
            <h3>Mark Attendance</h3>
            <form onSubmit={(e) => { e.preventDefault(); submitAttendance(); }}>
              <div className="formgroup_88748">
                <label>Date</label>
                <input 
                  type="date" 
                  value={attendanceForm.date}
                  onChange={(e) => setAttendanceForm({...attendanceForm, date: e.target.value})}
                  required
                />
              </div>
              
              <div className="formgroup_88748">
                <label>Check-in Time</label>
                <input 
                  type="time" 
                  value={attendanceForm.checkInTime}
                  onChange={(e) => setAttendanceForm({...attendanceForm, checkInTime: e.target.value})}
                  required
                />
              </div>
              
              <p className="infotext_88748">
                <FaMapMarkerAlt /> Your location will be recorded automatically
              </p>
              
              <div className="modalactions_88748">
                <button type="button" onClick={() => setShowAttendanceModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary_88748">
                  Submit Attendance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showActivityModal && (
        <div className="modaloverlay_88748" onClick={() => setShowActivityModal(false)}>
          <div className="modalcontent_88748 large_88748" onClick={(e) => e.stopPropagation()}>
            <h3>Log Daily Activity</h3>
            <form onSubmit={(e) => { e.preventDefault(); submitDailyActivity(); }}>
              <div className="formgroup_88748">
                <label>Date</label>
                <input 
                  type="date" 
                  value={activityForm.date}
                  onChange={(e) => setActivityForm({...activityForm, date: e.target.value})}
                  required
                />
              </div>
              
              <div className="formgroup_88748">
                <label>Tasks Performed</label>
                <textarea 
                  value={activityForm.tasks}
                  onChange={(e) => setActivityForm({...activityForm, tasks: e.target.value})}
                  placeholder="Describe the clinical tasks you performed today..."
                  rows="3"
                  required
                />
              </div>
              
              <div className="formrow_88748">
                <div className="formgroup_88748">
                  <label>Patients Seen</label>
                  <input 
                    type="number" 
                    min="0"
                    value={activityForm.patientsSeen}
                    onChange={(e) => setActivityForm({...activityForm, patientsSeen: e.target.value})}
                  />
                </div>
                
                <div className="formgroup_88748">
              <label>Procedures (comma-separated)</label>
              <input 
                type="text" 
                value={activityForm.proceduresPerformed}
                onChange={(e) => setActivityForm({...activityForm, proceduresPerformed: e.target.value})}
                placeholder="e.g., Suturing, IV Cannulation"
              />
            </div>
          </div>
          
          <div className="formgroup_88748">
            <label>Learning Points</label>
            <textarea 
              value={activityForm.learningPoints}
              onChange={(e) => setActivityForm({...activityForm, learningPoints: e.target.value})}
              placeholder="What did you learn today?"
              rows="2"
            />
          </div>
          
          <div className="formgroup_88748">
            <label>Additional Notes</label>
            <textarea 
              value={activityForm.notes}
              onChange={(e) => setActivityForm({...activityForm, notes: e.target.value})}
              placeholder="Any other observations or notes..."
              rows="2"
            />
          </div>
          
          <div className="modalactions_88748">
            <button type="button" onClick={() => setShowActivityModal(false)}>
              Cancel
            </button>
            <button type="submit" className="primary_88748">
              Submit Activity
            </button>
          </div>
        </form>
      </div>
    </div>
  )}

  {showReportModal && (
    <div className="modaloverlay_88748" onClick={() => setShowReportModal(false)}>
      <div className="modalcontent_88748 large_88748" onClick={(e) => e.stopPropagation()}>
        <h3>Submit Weekly Report</h3>
        <form onSubmit={(e) => { e.preventDefault(); submitWeeklyReport(); }}>
          <div className="formrow_88748">
            <div className="formgroup_88748">
              <label>Week Number</label>
              <input 
                type="number" 
                min="1"
                value={reportForm.weekNumber}
                onChange={(e) => setReportForm({...reportForm, weekNumber: parseInt(e.target.value)})}
                required
              />
            </div>
            
            <div className="formgroup_88748">
              <label>Week Start Date</label>
              <input 
                type="date" 
                value={reportForm.weekStartDate}
                onChange={(e) => setReportForm({...reportForm, weekStartDate: e.target.value})}
                required
              />
            </div>
            
            <div className="formgroup_88748">
              <label>Week End Date</label>
              <input 
                type="date" 
                value={reportForm.weekEndDate}
                onChange={(e) => setReportForm({...reportForm, weekEndDate: e.target.value})}
                required
              />
            </div>
          </div>
          
          <div className="formgroup_88748">
            <label>Weekly Summary</label>
            <textarea 
              value={reportForm.summary}
              onChange={(e) => setReportForm({...reportForm, summary: e.target.value})}
              placeholder="Summarize your week's activities..."
              rows="3"
              required
            />
          </div>
          
          <div className="formgroup_88748">
            <label>Key Learnings</label>
            <textarea 
              value={reportForm.keyLearnings}
              onChange={(e) => setReportForm({...reportForm, keyLearnings: e.target.value})}
              placeholder="What were the main things you learned this week?"
              rows="3"
              required
            />
          </div>
          
          <div className="formgroup_88748">
            <label>Challenges Faced</label>
            <textarea 
              value={reportForm.challenges}
              onChange={(e) => setReportForm({...reportForm, challenges: e.target.value})}
              placeholder="What challenges did you encounter?"
              rows="2"
            />
          </div>
          
          <div className="formgroup_88748">
            <label>Total Cases Handled</label>
            <input 
              type="number" 
              min="0"
              value={reportForm.casesHandled}
              onChange={(e) => setReportForm({...reportForm, casesHandled: parseInt(e.target.value)})}
              required
            />
          </div>
          
          <div className="modalactions_88748">
            <button type="button" onClick={() => setShowReportModal(false)}>
              Cancel
            </button>
            <button type="submit" className="primary_88748">
              Submit Report
            </button>
          </div>
        </form>
      </div>
    </div>
  )}
</div>
  );
};

export default DRPModule;